import { Fragment, useState } from 'react'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import { RescheduleRequestDisplay } from '../types/reschedule'
import { respondToRescheduleRequest } from '../lib/rescheduleService'

interface RescheduleApprovalModalProps {
  open: boolean
  onClose: () => void
  request: RescheduleRequestDisplay
  currentUserId: string
  onResponseSubmitted: () => void
}

export default function RescheduleApprovalModal({
  open,
  onClose,
  request,
  currentUserId,
  onResponseSubmitted
}: RescheduleApprovalModalProps) {
  const [responseMessage, setResponseMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isRequester = currentUserId === request.requestedBy
  const canRespond = currentUserId === request.requestedFor && request.status === 'pending' && !request.isExpired

  const handleResponse = async (approve: boolean) => {
    try {
      setSubmitting(true)
      setError(null)

      const result = await respondToRescheduleRequest(
        request.id,
        approve,
        responseMessage
      )

      if (result.success) {
        onResponseSubmitted()
        onClose()
        setResponseMessage('')
      } else {
        setError(result.error || 'Failed to submit response')
      }
    } catch (err) {
      console.error('Error submitting response:', err)
      setError('An unexpected error occurred')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!submitting) {
      setResponseMessage('')
      setError(null)
      onClose()
    }
  }

  const getStatusPill = () => {
    if (request.isExpired) {
      return (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
          style={{ background: 'rgba(255,255,255,0.06)', color: 'hsl(0 0% 65%)', boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
          Expired
        </span>
      )
    }

    switch (request.status) {
      case 'pending':
        return (
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
            style={{ background: 'rgba(255,180,50,0.1)', color: '#ffb432', boxShadow: 'inset 0 0 0 1px rgba(255,180,50,0.25)' }}>
            Pending
          </span>
        )
      case 'approved':
        return (
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
            style={{ background: 'rgba(100,220,150,0.1)', color: '#64dc96', boxShadow: 'inset 0 0 0 1px rgba(100,220,150,0.25)' }}>
            Approved
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
            style={{ background: 'rgba(255,138,115,0.1)', color: '#ff8a73', boxShadow: 'inset 0 0 0 1px rgba(255,138,115,0.25)' }}>
            Rejected
          </span>
        )
      case 'cancelled':
        return (
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'hsl(0 0% 65%)', boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
            Cancelled
          </span>
        )
      default:
        return null
    }
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px) saturate(120%)' }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 scale-[0.985]" enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 translate-y-2 scale-[0.985]"
            >
              <DialogPanel
                className="w-full max-w-[480px] overflow-hidden rounded-2xl relative"
                style={{ background: 'hsl(0 0% 5%)', border: '1px solid hsl(0 0% 20%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55)' }}
              >
                {/* Close button */}
                <button
                  type="button"
                  className="absolute right-4 top-4 rounded-full p-1.5 text-white/40 hover:text-white/80 hover:bg-white/[0.06] transition-colors z-10"
                  onClick={handleClose}
                  disabled={submitting}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>

                <div className="p-6 md:p-8">
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-full grid place-items-center mb-5"
                    style={{ background: 'rgba(255,255,255,0.05)', boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 10h17"/>
                      <path d="M10 15l2 2 4-4"/>
                    </svg>
                  </div>

                  {/* Title & status */}
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-[22px] font-medium tracking-tight">Reschedule <span className="font-serif italic font-normal">request</span></h3>
                    {getStatusPill()}
                  </div>

                  {/* Meeting title & requester */}
                  <div className="mt-1 mb-6">
                    <p className="text-white/90 text-[14px] font-medium">{request.meetingTitle}</p>
                    <p className="text-white/50 text-[12.5px] mt-0.5">
                      Requested by {isRequester ? 'you' : request.requestedByName}
                    </p>
                  </div>

                  {/* Time comparison */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {/* Original time */}
                    <div className="rounded-xl p-4"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                      <div className="text-[10px] tracking-[2px] uppercase text-white/40 mb-2.5">Current</div>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 text-white/70">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 10h17"/></svg>
                          <span className="text-[12.5px]">{request.originalDate}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/70">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                          <span className="text-[12.5px]">{request.originalTime}</span>
                        </div>
                      </div>
                    </div>

                    {/* New proposed time */}
                    <div className="rounded-xl p-4"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid hsl(0 0% 28%)' }}>
                      <div className="text-[10px] tracking-[2px] uppercase text-white/70 mb-2.5">Proposed</div>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 text-white">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 10h17"/></svg>
                          <span className="text-[12.5px] font-medium">{request.newDate}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                          <span className="text-[12.5px] font-medium">{request.newTime}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Duration pill */}
                  <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg mb-4"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-white/40"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                    <span className="text-[12px] text-white/50">Duration: <span className="text-white/80 font-medium">{request.duration} min</span></span>
                  </div>

                  {/* Reason */}
                  {request.reason && (
                    <div className="rounded-xl p-4 mb-4"
                      style={{ background: 'rgba(255,180,50,0.04)', border: '1px solid rgba(255,180,50,0.15)' }}>
                      <div className="text-[10px] tracking-[2px] uppercase text-white/40 mb-2">Reason</div>
                      <p className="text-white/80 text-[13px] leading-relaxed">&ldquo;{request.reason}&rdquo;</p>
                    </div>
                  )}

                  {/* Time remaining (for pending) */}
                  {request.status === 'pending' && !request.isExpired && (
                    <div className="flex items-center gap-2 mb-4">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-white/30"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                      <span className="text-[11.5px] text-white/40">{request.timeRemaining}</span>
                    </div>
                  )}

                  {/* Response message (when user can respond) */}
                  {canRespond && (
                    <div className="mb-5">
                      <label className="label">Message (optional)</label>
                      <textarea
                        className="field min-h-[72px] resize-none"
                        value={responseMessage}
                        onChange={(e) => setResponseMessage(e.target.value)}
                        placeholder="Add a note..."
                        disabled={submitting}
                      />
                    </div>
                  )}

                  {/* Error */}
                  {error && (
                    <div className="rounded-xl p-3.5 mb-5 flex items-start gap-2.5"
                      style={{ background: 'rgba(255,138,115,0.06)', border: '1px solid rgba(255,138,115,0.2)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff8a73" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                      <p className="text-[12.5px] leading-relaxed" style={{ color: '#ff8a73' }}>{error}</p>
                    </div>
                  )}

                  {/* Action buttons */}
                  {canRespond ? (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleResponse(false)}
                        disabled={submitting}
                        className="btn flex-1 justify-center !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: 'rgba(255,138,115,0.1)', color: '#ff8a73', boxShadow: 'inset 0 0 0 1px rgba(255,138,115,0.25)' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                        {submitting ? 'Processing...' : 'Decline'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleResponse(true)}
                        disabled={submitting}
                        className="btn btn-primary flex-1 justify-center !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>
                        {submitting ? 'Processing...' : 'Approve'}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={submitting}
                      className="btn btn-outline w-full justify-center !py-3"
                    >
                      Close
                    </button>
                  )}
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
